import { useMemo } from "react";
import { useSavedProperties } from "@/hooks/use-saved-properties";
import type { MockProperty } from "@/data/mockProperties";
import type { PropertyMeta } from "@/lib/property-meta";

/**
 * Address and photo for every pipeline property in the workspace, keyed by
 * `pipeline_properties.id` — the id work orders, payments, tenant messages and
 * PM settings all store as `pipeline_property_id`.
 *
 * Deliberately sourced from `savedProperties` rather than `usePortfolio()`:
 * the portfolio list is closed-won *minus* anything in active rehab, but a
 * property keeps its work orders and rent payments while it's being rehabbed
 * (and a rented listing isn't gated on portfolio membership at all). Keying off
 * the narrower list left those records with no address and no photo.
 */
export function usePropertyMeta(): Record<string, PropertyMeta> {
  const { savedProperties } = useSavedProperties();

  return useMemo(() => {
    const map: Record<string, PropertyMeta> = {};
    for (const saved of savedProperties) {
      // Property data may use camelCase (feed) or snake_case (older rows).
      const property = saved.property_data as
        | (MockProperty & { image_url?: string; street_address?: string })
        | undefined;
      map[saved.id] = {
        address:
          property?.address ||
          property?.street_address ||
          `Property ${saved.id.slice(0, 6)}`,
        imageUrl: property?.imageUrl || property?.image_url,
        city: property?.city,
        state: property?.state,
        zip: property?.zip,
      };
    }
    return map;
  }, [savedProperties]);
}
